import { useEffect } from 'react'
import { Ban, Monitor, Pencil } from 'lucide-react'
import { Link, useNavigate } from 'react-router'

import { useDesktopDevices, useUpdateDevice } from 'query/useDevices'
import { ConfirmNameModal, DataTable, EmptyState, Figures, Icon, IconButton, IconButtonLink, LoadingIndicator, PageContent, PageHeader } from 'ui'
import { formatRelativeTime } from 'utils/dates'
import { formatDeviceConnections } from 'utils/deviceTelemetry'
import { getPlatformDisplay, isMacPlatform, isWindowsPlatform } from 'utils/platforms'

const DisableDeviceModal = ({ id, name, onDeleteSuccess }) => {
  const { mutateAsync, isPending } = useUpdateDevice(id)

  return (
    <ConfirmNameModal
      actionLabel="Disable"
      confirmationAction="disable this device"
      isPending={isPending}
      name={name}
      onConfirm={async () => {
        await mutateAsync({ status: 'trashed' })
        onDeleteSuccess()
      }}
      requireNameConfirmation={Boolean(name)}
      title="Disable Device"
      trigger={(
        <IconButton label={name ? `Disable ${name}` : 'Disable Unnamed Device'} title="Disable Device" variant="danger">
          <Icon as={Ban} />
        </IconButton>
      )}
    >
      The device becomes unlicensed immediately. Its seat returns to your pool, and the user can register it again.
    </ConfirmNameModal>
  )
}

const DevicesPage = () => {
  const navigate = useNavigate()
  const { data: devices, invalidateQuery, isError, isLoading } = useDesktopDevices()

  useEffect(() => {
    if (isError) navigate('/users/sign_in')
  }, [isError, navigate])

  const macCount = devices.filter(device => isMacPlatform(device.platform)).length
  const windowsCount = devices.filter(device => isWindowsPlatform(device.platform)).length

  const columns = [
    {
      key: 'name',
      label: 'Device',
      primary: true,
      render: device => {
        const platform = getPlatformDisplay(device.platform)

        return (
          <div className="tw:space-y-2 tw:break-words">
            <span className="tw:block tw:font-semibold tw:text-foreground-strong">
              {device.name || 'Unnamed Device'}
            </span>
            <span className="tw:flex tw:items-center tw:gap-2">
              <Icon as={platform.icon} />
              <span>
                <span className="tw:block">{platform.label}</span>
                {platform.detail && <span className="ds-caption tw:block">{platform.detail}</span>}
              </span>
            </span>
            <span className="tw:block">App Version: {device.appVersion?.trim() || 'Not reported'}</span>
          </div>
        )
      },
    },
    {
      key: 'user',
      label: 'User',
      render: device => device.user
        ? <span className="tw:text-foreground-strong">{device.user.email}</span>
        : <span className="tw:text-foreground-subtle">Unnamed User</span>,
    },
    {
      key: 'configuration',
      label: 'Configuration',
      render: device => device.configuration
        ? <Link to={`/configurations/${device.configuration.id}`}>{device.configuration.name}</Link>
        : '—',
    },
    {
      key: 'connectionStats',
      label: 'Connections',
      span: true,
      render: device => {
        const connections = formatDeviceConnections(device.connectionStats)

        return connections.length ? (
          <ul className="tw:space-y-1 tw:break-words" role="list">
            {connections.map(connection => <li key={connection.type}>{connection.text}</li>)}
          </ul>
        ) : 'No connections reported'
      },
    },
    { key: 'lastSeen', label: 'Last Seen', render: device => formatRelativeTime(device.lastSeen) },
  ]

  return (
    <>
      <PageHeader
        subtitle="Monitor every device running ExpanDrive across your organization."
        title="Active Devices"
      />
      <PageContent>
        {isLoading ? <LoadingIndicator /> : (
          <>
            <Figures
              className="tw:mb-10"
              items={[
                { value: devices.length, label: 'Total Devices' },
                { value: macCount, label: 'macOS' },
                { value: windowsCount, label: 'Windows' },
              ]}
            />
            <DataTable
              actions={device => (
                <>
                  {device.user && (
                    <IconButtonLink label={`Edit ${device.user.email}`} title="Edit User" to={`/users/${device.user.id}`}>
                      <Icon as={Pencil} />
                    </IconButtonLink>
                  )}
                  <DisableDeviceModal id={device.id} name={device.name} onDeleteSuccess={invalidateQuery} />
                </>
              )}
              columns={columns}
              empty={(
                <EmptyState icon={<Icon as={Monitor} size={32} />} title="No Active Devices">
                  A device appears here after a user signs in to ExpanDrive.
                </EmptyState>
              )}
              footer="Connection counts reflect the last reported check-in, not current device availability."
              rows={devices}
            />
          </>
        )}
      </PageContent>
    </>
  )
}

export default DevicesPage
